import { ContentFragment, Data as ArticleData } from '@root/modules/article/types/article';
import articleConfig from '@root/modules/article/config/article.config';
import { Config } from '@root/modules/channel/types/channel';
import striptags from 'striptags';
import cloneDeep from 'lodash/cloneDeep';

type Fragment = ContentFragment;

interface FragmentsGroup {
  items: (Fragment | string)[];
  options: {
    skipAds: boolean;
    addRelatedHeadlines: boolean;
    relatedPosition: number;
  };
}

const initFragmentsGroup: FragmentsGroup = {
  items: [],
  options: {
    skipAds: false,
    addRelatedHeadlines: false,
    relatedPosition: 1,
  },
};

function isWideFragment(fragment: Fragment) {
  if (fragment.type === 'media' || fragment.type === 'embed' || fragment.type === 'image') {
    const { alignment = 'center', code = '' } = fragment.attrs;
    // TODO: Remove when content-api returns valid data
    const isWide = (code as string).includes('width-screen');
    if (alignment === 'screen' || alignment === 'fullWidth' || isWide) {
      return true;
    }
  }
  return false;
}

function getIndex(article: ArticleData, index: number) {
  if (article.primaryCategory && (article.primaryCategory.parentCategory.id === 79440681 || article.primaryCategory.parentCategory.id === 85827763)) {
    return index - 1;
  }

  return index;
}

const getArticleGroupedBody = (article: ArticleData, settings: Config['page']['article']['component']): FragmentsGroup[] => {
  const { relatedArticles, info } = settings;
  const topSection = article.settings?.articleTemplate || 'default';
  const skipBodyFragments = info.leadPosition === 'top' || topSection === 'smallImage' ? 1 : 0;
  const relatedArticlesSettings = relatedArticles;

  const body = article.content.body.content;
  const fragmentsSize = body.length;

  if (!body || fragmentsSize === 0) {
    return [];
  }

  const adsFrequency = articleConfig.ads.frequency;
  const group: FragmentsGroup[] = [];

  let charCounter = 0;
  let adInjected = false;
  let addRelatedHeadlines = true;
  let fragments: (Fragment | string)[] = [];
  let fragmentsGroup = cloneDeep(initFragmentsGroup);

  for (let i = 0; i < fragmentsSize; i++) {
    if (skipBodyFragments > 0 && i < skipBodyFragments) {
      continue;
    }
    const isLast = i === fragmentsSize - 1;
    const fragment = body[i];

    const text = striptags(fragment.html);
    let closeSubGroup = false;
    // TODO: Remove when content-api returns valid data
    if (isWideFragment(fragment)) {
      fragment.attrs.alignment = 'fullwidth';
    }
    // if fragment width is wide or viewport width close previous group and create new with single wide fragment without ads
    if (isWideFragment(fragment) && charCounter !== 0) {
      fragmentsGroup.items = fragments;
      group.push(fragmentsGroup);
      fragmentsGroup = cloneDeep(initFragmentsGroup);
      fragmentsGroup.options.skipAds = true;
      adInjected = false;
      charCounter = 0;
      fragments = [];
      closeSubGroup = true;
    }

    fragments.push(fragment);

    charCounter += text.length;

    if (adsFrequency <= charCounter && !adInjected) {
      adInjected = true;
      fragments.push('adFragment');
    }

    // close subGroup if chunk size >= frequency size or fragment is wide/viewport width or last iteration
    if (charCounter > adsFrequency || closeSubGroup || isLast) {
      fragmentsGroup.items = fragments;
      fragmentsGroup.options.addRelatedHeadlines = fragments.length > relatedArticlesSettings.minParagraphsLength && addRelatedHeadlines;

      // Calculate related headlines position in fragments group
      if (fragmentsGroup.options.addRelatedHeadlines) {
        fragmentsGroup.options.relatedPosition = relatedArticlesSettings.afterParagraphPosition;

        for (const [index, fragment] of fragments.entries()) {
          const isHTML = typeof fragment !== 'string' && !(fragment.type === 'sidebar' || fragment.type === 'pullout' || fragment.type === 'embed');
          if (isHTML && index >= fragmentsGroup.options.relatedPosition) {
            fragmentsGroup.options.relatedPosition = getIndex(article, index);
            break;
          }
        }
      }

      group.push(fragmentsGroup);
      fragmentsGroup = cloneDeep(initFragmentsGroup);

      addRelatedHeadlines = false;
      adInjected = false;
      charCounter = 0;
      fragments = [];
      closeSubGroup = false;
    }
  }

  return group;
};

export default getArticleGroupedBody;
